const API_KEY = import.meta.env.VITE_TMDB_API_KEY || '5d72a0ee3d073c888b3214a716ffb9e8'; 
const BASE_URL = import.meta.env.VITE_TMDB_BASE_URL || 'https://api.themoviedb.org/3';
const IMAGE_BASE_URL = import.meta.env.VITE_TMDB_IMAGE_BASE_URL || 'https://image.tmdb.org/t/p';

if (!API_KEY) {
  console.error('TMDB API key is missing. Please add VITE_TMDB_API_KEY to your .env file.');
}

const apiRequest = async (endpoint, params = {}) => {
  try {
    const url = new URL(`${BASE_URL}${endpoint}`);
    url.searchParams.append('api_key', API_KEY);
    url.searchParams.append('language', 'en-US');
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });

    const response = await fetch(url.toString());
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

export const tmdbApi = {
  // Get trending movies
  getTrending: (timeWindow = 'week') => 
    apiRequest('/trending/movie/' + timeWindow),

  // Get popular movies
  getPopular: (page = 1) => 
    apiRequest('/movie/popular', { page }),

  // Get popular TV series
  getPopularTV: (page = 1) =>
    apiRequest('/tv/popular', { page }),

  // Get top rated movies
  getTopRated: (page = 1) => 
    apiRequest('/movie/top_rated', { page }),

  // Get upcoming movies
  getUpcoming: (page = 1) => 
    apiRequest('/movie/upcoming', { page }),

  // Get now playing movies
  getNowPlaying: (page = 1) => 
    apiRequest('/movie/now_playing', { page }),

  // Search movies
  searchMovies: (query, page = 1) => 
    apiRequest('/search/movie', { query, page }),

  // Search multi (movies, TV, people)
  searchMulti: (query, page = 1) =>
    apiRequest('/search/multi', { query, page, include_adult: 'false' }),

  // Get movie details
  getMovieDetails: (movieId) => 
    apiRequest(`/movie/${movieId}`),

  // Get TV details
  getTVDetails: (tvId) =>
    apiRequest(`/tv/${tvId}`),

  // Get movie credits (cast & crew)
  getMovieCredits: (movieId) => 
    apiRequest(`/movie/${movieId}/credits`),

  // Get TV credits (cast & crew)
  getTVCredits: (tvId) =>
    apiRequest(`/tv/${tvId}/credits`),

  // Get movie videos (trailers, etc.)
  getMovieVideos: (movieId) => 
    apiRequest(`/movie/${movieId}/videos`),

  // Get TV videos
  getTVVideos: (tvId) =>
    apiRequest(`/tv/${tvId}/videos`),

  // Get a TV season with episodes
  getTVSeason: (tvId, seasonNumber) =>
    apiRequest(`/tv/${tvId}/season/${seasonNumber}`),

  // Get movie certifications (release dates per country)
  getMovieReleaseDates: (movieId) =>
    apiRequest(`/movie/${movieId}/release_dates`),

  // Get TV content ratings
  getTVContentRatings: (tvId) =>
    apiRequest(`/tv/${tvId}/content_ratings`),

  // Get similar movies
  getSimilarMovies: (movieId, page = 1) => 
    apiRequest(`/movie/${movieId}/similar`, { page }),

  // Get similar TV
  getSimilarTV: (tvId, page = 1) =>
    apiRequest(`/tv/${tvId}/similar`, { page }),

  // Get movie recommendations
  getMovieRecommendations: (movieId, page = 1) => 
    apiRequest(`/movie/${movieId}/recommendations`, { page }),

  // Get genres
  getGenres: () => 
    apiRequest('/genre/movie/list'),

  // Get movies by genre
  getMoviesByGenre: (genreId, page = 1) => 
    apiRequest('/discover/movie', { 
      with_genres: genreId, 
      page,
      sort_by: 'popularity.desc'
    }),

  // Get movie images
  getMovieImages: (movieId) => 
    apiRequest(`/movie/${movieId}/images`),

  // Get anime movies using keyword id 210024 (Anime)
  getAnimeMovies: (page = 1) =>
    apiRequest('/discover/movie', {
      with_keywords: '210024',
      page,
      sort_by: 'popularity.desc'
    }),

  // Discover by production companies (movies)
  discoverMoviesByCompanies: (companyIds, page = 1) =>
    apiRequest('/discover/movie', {
      with_companies: companyIds,
      page,
      sort_by: 'popularity.desc'
    }),

  // Discover TV by production companies
  discoverTVByCompanies: (companyIds, page = 1) =>
    apiRequest('/discover/tv', {
      with_companies: companyIds,
      page,
      sort_by: 'popularity.desc'
    }),

  // Discover TV by networks
  discoverTVByNetworks: (networkIds, page = 1) =>
    apiRequest('/discover/tv', {
      with_networks: networkIds,
      page,
      sort_by: 'popularity.desc'
    }),
};

// Image URL helpers
export const getImageUrl = (path, size = 'w500') => {
  if (!path) return null;
  return `${IMAGE_BASE_URL}/${size}${path}`;
};

export const getPosterUrl = (path, size = 'w500') => getImageUrl(path, size);
export const getBackdropUrl = (path, size = 'w1280') => getImageUrl(path, size);
export const getProfileUrl = (path, size = 'w185') => getImageUrl(path, size);

// Utility functions
export const formatRuntime = (minutes) => {
  if (!minutes) return 'N/A';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

export const formatReleaseDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatVoteAverage = (vote) => {
  if (!vote && vote !== 0) return 'N/A';
  return Number(vote).toFixed(1);
};
